import { Button, InlineNotification } from '@carbon/react';
import styles from './reconcile-wells-tearsheet.module.scss';

export interface ReconcileActionBarProps {
  /** Why the last reconcile was refused, when one was. */
  commitError: string | null;
  /** A reconcile is in flight; the bar waits on it. */
  committing: boolean;
  /** Whether the authored structure has anything to commit. */
  canReconcile: boolean;
  /**
   * Whether reconcile is enabled in this environment (WELL_DEDUPE_RECONCILE_ENABLED). When off, the
   * operator can still explore the sheet, but submitting is disabled and an info notice explains why
   * (the endpoint itself refuses with 403 as the backstop).
   */
  reconcileEnabled: boolean;
  onCancel: () => void;
  onReconcile: () => void;
}

/**
 * The reconcile sheet's bottom action bar: the commit error (when one), Cancel, and Reconcile.
 * @param {ReconcileActionBarProps} props The commit state and the two actions.
 * @return {JSX.Element} The action bar.
 */
export function ReconcileActionBar({
  commitError,
  committing,
  canReconcile,
  reconcileEnabled,
  onCancel,
  onReconcile,
}: ReconcileActionBarProps) {
  return (
    <div className={styles.footer}>
      {!reconcileEnabled ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          className={styles.footerError}
          title="Reconcile is turned off"
          subtitle="You can review this sheet, but changes can't be submitted in this environment."
        />
      ) : null}
      {commitError ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className={styles.footerError}
          title="Reconcile failed"
          subtitle={commitError}
        />
      ) : null}
      <Button kind="ghost" onClick={onCancel} disabled={committing}>
        Cancel
      </Button>
      <Button
        kind="primary"
        disabled={!reconcileEnabled || !canReconcile || committing}
        onClick={onReconcile}
      >
        {committing ? 'Reconciling…' : 'Reconcile'}
      </Button>
    </div>
  );
}
